import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import api from '../../lib/api';
import { fromSlug } from '../../lib/slug';
import { useAuthStore } from '../../stores/authStore';
import type { WorkspaceResponse, ScriptSiblingRow } from './scriptWorkspaceTypes';
import ScriptWorkspaceStudioView from './ScriptWorkspaceStudioView';
import ScriptWorkspaceSkeleton from './ScriptWorkspaceSkeleton';
import { AIContentQAPanel } from '../../components/AIContentQAPanel';
import { GoogleWorkspaceJobPanel } from '../../components/google/GoogleWorkspaceJobPanel';
import { KolStoryboardViewer } from '../../components/google/KolStoryboardViewer';

interface Props {
  scriptsIndexPath: string;
  headerBackUsesHistory?: boolean;
  breadcrumbScriptsLabel?: string;
}

export default function ScriptWorkspace({
  scriptsIndexPath,
  headerBackUsesHistory = false,
  breadcrumbScriptsLabel = 'Scripts',
}: Props) {
  const { id: rawId } = useParams<{ id: string }>();
  const id = fromSlug(rawId || '');
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);

  const [actionLoading, setActionLoading] = useState(false);
  const [editContent, setEditContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [revisionError, setRevisionError] = useState('');
  const [brandRevisionMode, setBrandRevisionMode] = useState(false);
  const [revisionCommentDraft, setRevisionCommentDraft] = useState('');
  const [siblingScripts, setSiblingScripts] = useState<ScriptSiblingRow[]>([]);
  const [gateBusy, setGateBusy] = useState(false);

  const workspaceQuery = useQuery({
    queryKey: ['script', 'workspace', id],
    queryFn: async () => {
      const res = await api.get<WorkspaceResponse>(`/scripts/${id}/workspace`);
      return res.data;
    },
    enabled: Boolean(id),
    staleTime: 60 * 1000,
  });

  const data = workspaceQuery.data ?? null;

  const reloadWorkspace = useCallback(async () => {
    await workspaceQuery.refetch();
  }, [workspaceQuery.refetch]);

  useEffect(() => {
    const s = workspaceQuery.data?.script;
    if (!s) return;
    setEditContent(s.content || '');
    setSiblingScripts(workspaceQuery.data?.siblingScripts ?? []);
    setRevisionError('');
  }, [
    id,
    workspaceQuery.data?.script?.updatedAt,
    workspaceQuery.data?.script?.status,
    workspaceQuery.data?.script?.version,
  ]);

  useEffect(() => {
    setBrandRevisionMode(false);
    setRevisionCommentDraft('');
    setRevisionError('');
  }, [id]);

  const script = data?.script;
  const role = user?.role;
  const pipeline = data?.pipeline;

  const strictPipeline = Boolean(pipeline?.strictPipeline);
  const kolScriptApproved = Boolean(
    pipeline?.kolScriptApprovedAt ?? script?.invitation?.kolScriptApprovedAt
  );
  const agencySubmitRequiredBeforeBrand = Boolean(
    pipeline?.agencySubmitRequiredBeforeBrand ??
      script?.invitation?.agencySubmitRequiredBeforeBrand
  );

  const canEditContent = useMemo(() => {
    if (!script || !role) return false;
    if (role === 'BRAND') {
      return script.status === 'SUBMITTED_FOR_BRAND' && brandRevisionMode;
    }
    const editable =
      script.status === 'DRAFT' ||
      script.status === 'REVISION_REQUESTED' ||
      script.status === 'SUBMITTED_FOR_BRAND';
    if (!editable) return false;
    return role === 'KOL' || role === 'AGENCY' || role === 'ADMIN';
  }, [script, role, brandRevisionMode]);

  const canSubmitForReview = useMemo(() => {
    if (!script || !role) return false;
    if (role === 'BRAND') return false;
    if (script.status !== 'DRAFT' && script.status !== 'REVISION_REQUESTED') return false;
    if (strictPipeline && !kolScriptApproved) return false;
    // KOL approved with edits → only the agency (or admin) may submit.
    if (agencySubmitRequiredBeforeBrand && role === 'KOL') return false;
    return role === 'KOL' || role === 'AGENCY' || role === 'ADMIN';
  }, [script, role, strictPipeline, kolScriptApproved, agencySubmitRequiredBeforeBrand]);

  const canBrandReview = script?.status === 'SUBMITTED_FOR_BRAND' && (role === 'BRAND' || role === 'ADMIN');

  const canKolApproveScript = useMemo(() => {
    if (!script || role !== 'KOL') return false;
    if (!strictPipeline) return false;
    if (script.status !== 'DRAFT' && script.status !== 'REVISION_REQUESTED') return false;
    return !kolScriptApproved;
  }, [script, role, strictPipeline, kolScriptApproved]);

  const canAgencyRequestKolRevision = useMemo(() => {
    if (!script || (role !== 'AGENCY' && role !== 'ADMIN')) return false;
    if (!strictPipeline) return false;
    if (script.status !== 'DRAFT' && script.status !== 'REVISION_REQUESTED') return false;
    return kolScriptApproved;
  }, [script, role, strictPipeline, kolScriptApproved]);

  const kolName =
    script?.invitation?.kolProfile?.user?.fullName ?? script?.invitation?.kolProfile?.name ?? '—';

  const goBack = () => {
    if (headerBackUsesHistory) {
      navigate(-1);
    } else {
      navigate(scriptsIndexPath);
    }
  };

  const handleSave = async () => {
    if (!editContent.trim() || !script) return;
    try {
      setSaving(true);
      await api.put(`/scripts/${script.id}`, { content: editContent });
      await reloadWorkspace();
    } catch (e) {
      console.error(e);
      alert('Failed to save script');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    if (!script) return;
    if (!confirm('Submit this script for brand review?')) return;
    try {
      setSubmitting(true);
      await api.post(`/scripts/${script.id}/submit`);
      await reloadWorkspace();
    } catch (e: unknown) {
      console.error(e);
      const msg = (e as { response?: { data?: { error?: string } } })?.response?.data?.error;
      alert(msg || 'Failed to submit script');
    } finally {
      setSubmitting(false);
    }
  };

  const handleApprove = async () => {
    if (!script) return;
    setActionLoading(true);
    try {
      await api.post(`/scripts/${script.id}/approve`);
      await reloadWorkspace();
    } catch (e) {
      console.error(e);
    } finally {
      setActionLoading(false);
    }
  };

  const handleKolApproveScript = async () => {
    if (!script) return;
    setGateBusy(true);
    try {
      await api.post(`/scripts/${script.id}/kol-approve-script`);
      await reloadWorkspace();
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { error?: string } } })?.response?.data?.error;
      alert(msg || 'Failed to record KOL approval');
    } finally {
      setGateBusy(false);
    }
  };

  const handleAgencyRequestKolRevision = async () => {
    if (!script) return;
    if (!confirm('Start another KOL review round? KOL approval will be cleared until they approve again.')) return;
    setGateBusy(true);
    try {
      await api.post(`/scripts/${script.id}/agency-request-kol-revision`, {});
      await reloadWorkspace();
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { error?: string } } })?.response?.data?.error;
      alert(msg || 'Failed to request KOL revision');
    } finally {
      setGateBusy(false);
    }
  };

  const enterBrandRevisionMode = () => {
    if (!script) return;
    setBrandRevisionMode(true);
    setRevisionCommentDraft('I have updated the script.');
    setRevisionError('');
    setEditContent(script.content || '');
  };

  const cancelBrandRevisionMode = () => {
    if (!script) return;
    setBrandRevisionMode(false);
    setRevisionCommentDraft('');
    setRevisionError('');
    setEditContent(script.content || '');
  };

  const confirmBrandRevision = async (): Promise<boolean> => {
    if (!script) return false;
    const trimmed = revisionCommentDraft.trim();
    if (!trimmed) {
      setRevisionError('Please add a short summary for the KOL (what should change).');
      return false;
    }
    setRevisionError('');
    setActionLoading(true);
    try {
      if (editContent !== (script.content || '')) {
        await api.put(`/scripts/${script.id}`, { content: editContent });
      }
      await api.post(`/scripts/${script.id}/request-revision`, { reviewComment: trimmed });
      setBrandRevisionMode(false);
      setRevisionCommentDraft('');
      await reloadWorkspace();
      return true;
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setRevisionError(msg || 'Failed to request revision.');
      return false;
    } finally {
      setActionLoading(false);
    }
  };

  if (!id) {
    return <div className="text-center py-12 text-gray-500">Invalid script link.</div>;
  }

  if (workspaceQuery.isPending) {
    return <ScriptWorkspaceSkeleton />;
  }

  if (workspaceQuery.isError || !data || !script) {
    return <div className="text-center py-12 text-gray-500">Script not found or you do not have access.</div>;
  }

  return (
    <div className="space-y-6">
      {(role === 'AGENCY' || role === 'BRAND' || role === 'ADMIN') && script && (
        <AIContentQAPanel
          mode="script"
          entityId={script.id}
          description="Check script against campaign brief for brand voice, compliance, and key messages before review."
          onUseRevisionFeedback={(feedback) => {
            setRevisionCommentDraft(feedback);
            setBrandRevisionMode(true);
          }}
        />
      )}
      {script.invitation && role === 'KOL' && (
        <KolStoryboardViewer jobId={script.job.id} invitationId={script.invitation.id} />
      )}
      {script.invitation && role !== 'KOL' && (
        <GoogleWorkspaceJobPanel
          jobId={script.job.id}
          invitationId={script.invitation.id}
          scriptId={script.id}
          showBrief={false}
          showFolder={false}
          showStoryboard
          readOnly={role === 'BRAND'}
          className="border-0 shadow-none"
          onStoryboardImported={(content) => {
            setEditContent(content);
            void reloadWorkspace();
          }}
          onCommentsImported={() => void reloadWorkspace()}
        />
      )}
      <ScriptWorkspaceStudioView
      data={data}
      script={script}
      siblings={siblingScripts}
      scriptsIndexPath={scriptsIndexPath}
      breadcrumbScriptsLabel={breadcrumbScriptsLabel}
      role={role}
      kolName={kolName}
      currentUserId={user?.id}
      navigate={(path) => void navigate(path)}
      goBack={goBack}
      reloadWorkspace={reloadWorkspace}
      editContent={editContent}
      setEditContent={setEditContent}
      saving={saving}
      submitting={submitting}
      actionLoading={actionLoading}
      gateBusy={gateBusy}
      revisionError={revisionError}
      setRevisionError={setRevisionError}
      revisionCommentDraft={revisionCommentDraft}
      setRevisionCommentDraft={setRevisionCommentDraft}
      brandRevisionMode={brandRevisionMode}
      onEnterBrandRevisionMode={enterBrandRevisionMode}
      onCancelBrandRevisionMode={() => cancelBrandRevisionMode()}
      onConfirmBrandRevision={() => confirmBrandRevision()}
      canEditContent={canEditContent}
      canSubmitForReview={canSubmitForReview}
      canBrandReview={canBrandReview}
      canKolApproveScript={canKolApproveScript}
      canAgencyRequestKolRevision={canAgencyRequestKolRevision}
      onKolApproveScript={() => void handleKolApproveScript()}
      onAgencyRequestKolRevision={() => void handleAgencyRequestKolRevision()}
      handleSave={() => void handleSave()}
      handleSubmit={() => void handleSubmit()}
      handleApprove={() => void handleApprove()}
    />
    </div>
  );
}
